// src/hooks/useRichEditor.js
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tracks the current selection inside a contentEditable element so toolbar
 * buttons can act on it after focus shifts.
 */
export const useRichEditor = ({ initialHtml = '' } = {}) => {
  const editorRef = useRef(null);
  const savedRangeRef = useRef(null);
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikeThrough: false,
    code: false,
    mark: false,
    align: 'left',
    block: 'p',
  });

  /* Save selection whenever it changes inside the editor */
  const saveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (editorRef.current?.contains(range.commonAncestorContainer)) {
      savedRangeRef.current = range.cloneRange();
    }
  }, []);

  /* Restore selection before running a command */
  const restoreSelection = useCallback(() => {
    const sel = window.getSelection();
    if (!sel) return;
    sel.removeAllRanges();
    if (savedRangeRef.current) {
      sel.addRange(savedRangeRef.current);
    } else if (editorRef.current) {
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      sel.addRange(range);
    }
  }, []);

  /* Read current active formats at the caret */
  const refreshActiveFormats = useCallback(() => {
    if (!editorRef.current) return;
    const query = (cmd) => {
      try {
        return document.queryCommandState(cmd);
      } catch {
        return false;
      }
    };
    const block = (() => {
      try {
        const b = document.queryCommandValue('formatBlock');
        return (b || 'p').toLowerCase();
      } catch {
        return 'p';
      }
    })();
    const align = (() => {
      if (query('justifyCenter')) return 'center';
      if (query('justifyRight')) return 'right';
      if (query('justifyFull')) return 'justify';
      return 'left';
    })();
    setActiveFormats({
      bold: query('bold'),
      italic: query('italic'),
      underline: query('underline'),
      strikeThrough: query('strikeThrough'),
      code: !!window.getSelection()?.anchorNode?.parentElement?.closest?.('code'),
      mark: !!window.getSelection()?.anchorNode?.parentElement?.closest?.('mark'),
      align,
      block,
    });
  }, []);

  /* Run a command, keeping focus + selection intact */
  const exec = useCallback(
    (command, value = null) => {
      editorRef.current?.focus();
      restoreSelection();
      try {
        document.execCommand(command, false, value);
      } catch (err) {
        console.warn('execCommand failed:', command, err);
      }
      saveSelection();
      refreshActiveFormats();
    },
    [restoreSelection, saveSelection, refreshActiveFormats],
  );

  /* Wrap current selection with an arbitrary inline tag (mark, code) */
  const wrapSelection = useCallback(
    (tagName) => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (range.collapsed) return; // nothing selected

      const existing = range.commonAncestorContainer?.parentElement?.closest?.(tagName);
      if (existing) {
        // Toggle off: unwrap
        const parent = existing.parentNode;
        while (existing.firstChild) parent.insertBefore(existing.firstChild, existing);
        parent.removeChild(existing);
      } else {
        const el = document.createElement(tagName);
        try {
          range.surroundContents(el);
        } catch {
          // Range spans multiple elements → use extractContents
          const frag = range.extractContents();
          el.appendChild(frag);
          range.insertNode(el);
        }
      }
      saveSelection();
      refreshActiveFormats();
    },
    [saveSelection, refreshActiveFormats],
  );

  /* Set foreground / background color */
  const setColor = useCallback(
    (hex) => {
      exec('foreColor', hex);
    },
    [exec],
  );

  const setHighlight = useCallback(
    (hex) => {
      // hiliteColor works better than backColor for contentEditable
      editorRef.current?.focus();
      restoreSelection();
      try {
        document.execCommand('hiliteColor', false, hex);
      } catch {
        document.execCommand('backColor', false, hex);
      }
      saveSelection();
      refreshActiveFormats();
    },
    [restoreSelection, saveSelection, refreshActiveFormats],
  );

  /* Insert link around selection */
  const insertLink = useCallback(
    (url) => {
      const v = url?.trim();
      if (!v) return;
      exec('createLink', /^(https?:|mailto:|\/|#)/i.test(v) ? v : `https://${v}`);
    },
    [exec],
  );

  /* Clear all formatting from selection */
  const clearFormat = useCallback(() => {
    exec('removeFormat');
    // removeFormat does not strip <mark>/<code>; unwrap them manually
    const sel = window.getSelection();
    if (sel?.rangeCount) {
      const container = sel.getRangeAt(0).commonAncestorContainer;
      const el = container.nodeType === 1 ? container : container.parentElement;
      el?.querySelectorAll?.('mark, code').forEach((node) => {
        const p = node.parentNode;
        while (node.firstChild) p.insertBefore(node.firstChild, node);
        p.removeChild(node);
      });
    }
  }, [exec]);

  /* Set block format (p, h1, h2, h3, blockquote, pre) */
  const setBlock = useCallback(
    (tag) => {
      exec('formatBlock', `<${tag}>`);
    },
    [exec],
  );

  /* Wire listeners */
  useEffect(() => {
    const onSelectionChange = () => {
      saveSelection();
      refreshActiveFormats();
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [saveSelection, refreshActiveFormats]);

  /* Expose imperative getter for save */
  const getHtml = useCallback(() => editorRef.current?.innerHTML || '', []);

  return {
    editorRef,
    activeFormats,
    exec,
    wrapSelection,
    setColor,
    setHighlight,
    insertLink,
    clearFormat,
    setBlock,
    getHtml,
    saveSelection,
    restoreSelection,
  };
};